<script setup>
import {Gender, GenderLabel} from "@/choices";
import {mdiGenderFemale, mdiGenderMale, mdiHuman, mdiHumanChild} from "@mdi/js";
import {gettext} from "@/utils/translate";
import {isStandardBirthNumber} from "@/components/hpo/patientModal/tabs/identification/utils";
import {PoliceVerification, PoliceVerificationButtonLabel} from "@/components/hpo/choices";
import {insuranceChoices} from "@/components/hpo/identification/insurance";
import CountrySelect from "@/components/hpo/identification/CountrySelect.vue";
import {computed, watch} from "vue";
import dayjs from "dayjs";
import SelectionButtonsInput from "../../forms/SelectionButtonsInput.vue";
import SelectionButton from "../../forms/SelectionButton.vue";
import {pgettext} from "../../../utils/translate";
import {IsChild, IsChildLabel} from "../../../choices";


const gender = defineModel('gender');
const isChild = defineModel('isChild');
const country = defineModel('country');
const countryUnknown = defineModel('countryUnknown');
const firstName = defineModel('firstName');
const lastName = defineModel('lastName');
const birthDay = defineModel('birthDay');
const birthMonth = defineModel('birthMonth');
const birthYear = defineModel('birthYear');
const birthNumber = defineModel('birthNumber');
const nonStandardBirthNumber = defineModel('nonStandardBirthNumber');
const insurance = defineModel('insurance');
const address = defineModel('address');
const city = defineModel('city');
const phoneNumber = defineModel('phoneNumber');
const note = defineModel('note');
const policeVerification = defineModel('policeVerification');

const props = defineProps({
    formProps: {
        type: Object,
        required: true,
    },
    validateField: {
        type: Function,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
});
const emit = defineEmits(['submit']);

const birthDateIsValid = computed(() => {
    const now = dayjs();
    if (birthDay.value && birthMonth.value && birthYear.value) {
        return dayjs(new Date(birthYear.value, birthMonth.value - 1, birthDay.value)) <= now;
    }
    return true
});

const identificationComplete = computed(() => {
    return firstName.value && lastName.value && birthDay.value && birthMonth.value && birthYear.value;
});

watch(insurance, (newVal) => {
    if (newVal && typeof newVal === 'object') {
        insurance.value = newVal.code;
    }
});

async function submit() {
    let allValid = true;
    for (const field of Object.keys(props.formProps)) {
        const {valid} = await props.validateField(field);
        allValid = allValid && valid;
    }

    if (!allValid) {
        return;
    }

    emit('submit', {});
}
</script>

<template>
    <main>
        <h1>{{ pgettext('label', 'Identification') }}</h1>

        <div>
            <v-card :title="pgettext('label', 'Country')">
                <v-card-text>
                    <CountrySelect v-model:country="country"
                                   v-model:countryUnknown="countryUnknown"
                                   :countries="countries"
                                   :fieldProps="formProps.country"/>
                </v-card-text>
            </v-card>
            <v-card>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-card :title="pgettext('label', 'Gender')">
                            <v-card-text>
                                <v-row>
                                    <v-col>
                                        <SelectionButtonsInput v-model="gender" v-bind="formProps.gender" size="large">
                                            <SelectionButton :value="Gender.MALE" :prependIcon="mdiGenderMale"
                                                             color="primary">
                                                <strong>{{ GenderLabel[Gender.MALE] }}</strong>
                                            </SelectionButton>

                                            <SelectionButton :value="Gender.FEMALE" :prependIcon="mdiGenderFemale"
                                                             color="red">
                                                <strong>{{ GenderLabel[Gender.FEMALE] }}</strong>
                                            </SelectionButton>
                                        </SelectionButtonsInput>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card :title="pgettext('label', 'Adult / child')">
                            <v-card-text>
                                <v-row>
                                    <v-col>
                                        <SelectionButtonsInput v-model="isChild" v-bind="formProps.isChild"
                                                               size="large">
                                            <SelectionButton :value="IsChild.ADULT" color="primary"
                                                             :prepend-icon="mdiHuman">
                                                <strong>{{ IsChildLabel[IsChild.ADULT] }}</strong>
                                            </SelectionButton>
                                            <SelectionButton :value="IsChild.CHILD" color="pink"
                                                             :prepend-icon="mdiHumanChild">
                                                <strong>{{ IsChildLabel[IsChild.CHILD] }}</strong>
                                            </SelectionButton>
                                        </SelectionButtonsInput>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card>

            <v-card :title="pgettext('label', 'Personal data')">
                <v-card-text>
                    <!-- NAME & INSURANCE-->
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-text-field v-model="firstName"
                                          v-bind="formProps.firstName"
                                          autocomplete="off"
                                          :label="pgettext('label', 'First name')"/>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field v-model="lastName"
                                          v-bind="formProps.lastName"
                                          autocomplete="off"
                                          :label="pgettext('label', 'Last name')"/>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-combobox
                                v-model="insurance"
                                v-bind="formProps.insurance"
                                :label="pgettext('label', 'Insurance')"
                                :items="insuranceChoices"
                                item-title="name"
                            ></v-combobox>
                        </v-col>
                    </v-row>
                    <!-- BIRTH DATE -->
                    <v-row>
                        <v-alert v-if="!birthDateIsValid"
                                 color="error"
                                 icon="$error"
                                 :title="gettext('Date of birth is in future')"
                        ></v-alert>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <!-- Birth day -->
                                    <v-number-input v-model="birthDay" v-bind="formProps.birthDay"
                                                    :label="pgettext('label', 'Birth day')"
                                                    :min="1" :step="1"
                                                    control-variant="hidden"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <!-- Birth month -->
                                    <v-number-input v-model="birthMonth" v-bind="formProps.birthMonth"
                                                    :label="pgettext('label', 'Birth month')"
                                                    :min="1" :step="1"
                                                    control-variant="hidden"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <!-- Birth year -->
                                    <v-number-input v-model="birthYear" v-bind="formProps.birthYear"
                                                    :label="pgettext('label', 'Birth year')"
                                                    :min="1900" :step="1"
                                                    control-variant="hidden"/>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>

                    <!-- BIRTH NUMBER -->
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="birthNumber"
                                          v-bind="formProps.birthNumber"
                                          type="number"
                                          autocomplete="off"
                                          :label="pgettext('label', 'Birth number')"/>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-checkbox-btn
                                v-if="birthNumber && !isStandardBirthNumber(birthNumber)"
                                v-model="nonStandardBirthNumber"
                                v-bind="formProps.nonStandardBirthNumber"
                                :label="pgettext('label', 'Nonstandard birth number')"
                            ></v-checkbox-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card :title="pgettext('title', 'Other information')">
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="address"
                                          v-bind="formProps.address"
                                          autocomplete="off"
                                          :label="pgettext('label', 'Address')"/>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="city"
                                          v-bind="formProps.city"
                                          autocomplete="off"
                                          :label="pgettext('label', 'City')"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="phoneNumber"
                                          v-bind="formProps.phoneNumber"
                                          autocomplete="off"
                                          :label="pgettext('label', 'Phone number')"/>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="note"
                                          v-bind="formProps.note"
                                          autocomplete="off"
                                          :label="pgettext('label', 'Note')"/>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card :title="pgettext('title', 'Police verification')">
                <v-card-text>
                    <v-card-subtitle v-if="!identificationComplete" style="white-space: normal;">
                        <strong class="text-error">
                            ({{
                                gettext('To enable the "Verified by the police" option please enter at least first name, last name and birth date')
                            }})
                        </strong>
                    </v-card-subtitle>
                    <v-row>
                        <v-col>
                            <v-radio-group v-model="policeVerification" v-bind="formProps.policeVerification">
                                <v-radio color="primary"
                                         :value="PoliceVerification.UNVERIFIED"
                                         :label="PoliceVerificationButtonLabel[PoliceVerification.UNVERIFIED]"
                                ></v-radio>
                                <v-radio color="success"
                                         :value="PoliceVerification.VERIFIED"
                                         :label="PoliceVerificationButtonLabel[PoliceVerification.VERIFIED]"
                                         :disabled="!identificationComplete">
                                </v-radio>
                                <v-radio color="red"
                                         :value="PoliceVerification.UNABLE"
                                         :label="PoliceVerificationButtonLabel[PoliceVerification.UNABLE]"
                                ></v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <p class="text-center mt-6">
                <v-btn size="large" block @click="submit()">
                    <strong>{{ pgettext('action', 'Continue') }}</strong>
                </v-btn>
            </p>
        </div>
    </main>
</template>
